import { LogManager } from '@src/utility/Logger';

import {
  resolveSoftBreakToken,
  togglePhraseSoftBreak,
  toggleTokenSoftBreak,
} from '../../services/captionSoftBreaks';
import { AbstractCommand } from '../AbstractCommand';

import type { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import type {
  CaptionSoftBreakAnchor,
  PhraseSoftBreak,
} from '../../services/captionSoftBreaks';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:softBreak');

/**
 * PLAT-11914: add a soft line break inside a caption, or remove the one
 * already there. The caption stays one clip; only its soft-break list
 * changes (see `captionSoftBreaks.ts`). Edits the clip in place, as
 * `HideInKaraokeCommand` does: a clone would deep-copy `transcriptTokens`,
 * and synthetic-split siblings are recognised by sharing that array.
 */
export class ToggleCaptionSoftBreakCommand extends AbstractCommand {
  private anchor: CaptionSoftBreakAnchor;
  private clip: KaraokeClip | null = null;
  private prev: {
    tokenIds?: string[];
    phrasePositions?: PhraseSoftBreak[];
  } | null = null;

  constructor(editor: VideoEditor, anchor: CaptionSoftBreakAnchor) {
    super(editor);
    this.anchor = anchor;
  }

  getName(): string {
    return 'ToggleCaptionSoftBreakCommand';
  }

  getDescription(): string {
    return 'tokenId' in this.anchor
      ? `Toggle caption soft break after token ${this.anchor.tokenId}`
      : `Toggle caption soft break at phrase ${this.anchor.phraseIndex}, offset ${this.anchor.offset}`;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const anchor = this.anchor;
    const clipIndex = track.karaokeClips.findIndex(clip =>
      'tokenId' in anchor
        ? clip.transcriptTokens.some(token => token.id === anchor.tokenId)
        : clip.id === anchor.clipId,
    );
    if (clipIndex < 0) {
      logger.warn('execute: caption not found', { anchor });
      return false;
    }
    const clip = track.karaokeClips[clipIndex];

    let tokenIds = clip.softBreakAfterTokenIds;
    let phrasePositions = clip.softBreakPhrasePositions;
    if ('tokenId' in anchor) {
      const tokenId = resolveSoftBreakToken(
        clip.transcriptTokens,
        anchor.tokenId,
      );
      // The last word of a caption: a hard break already ends the line.
      if (!tokenId) return false;
      tokenIds = toggleTokenSoftBreak(
        tokenIds ?? [],
        tokenId,
        clip.transcriptTokens,
      );
    } else {
      if (!clip.translatedPhrases?.[anchor.phraseIndex]) return false;
      const breaks = togglePhraseSoftBreak(phrasePositions ?? [], {
        phraseIndex: anchor.phraseIndex,
        offset: anchor.offset,
      });
      if (!breaks) return false;
      phrasePositions = breaks;
    }

    this.clip = clip;
    this.prev = {
      tokenIds: clip.softBreakAfterTokenIds,
      phrasePositions: clip.softBreakPhrasePositions,
    };
    clip.softBreakAfterTokenIds = tokenIds?.length ? tokenIds : undefined;
    clip.softBreakPhrasePositions = phrasePositions?.length
      ? phrasePositions
      : undefined;
    this.editor.markDirty('caption_soft_break_toggled');
    return true;
  }

  undo(): boolean {
    if (!this.clip || !this.prev) return false;
    this.clip.softBreakAfterTokenIds = this.prev.tokenIds;
    this.clip.softBreakPhrasePositions = this.prev.phrasePositions;
    this.editor.markDirty('caption_soft_break_toggled_undo');
    return true;
  }
}
